import { useMemo } from 'react';
import { TIME_RANGE_PRESETS, parseTimeExpression } from './time_range_utils';

/**
 * Custom hook to manage time range state and calculate timestamps
 *
 * @param {Object|null} timeRangeValue - The time range value from state
 *   - For presets: { mode: 'preset', presetId: 'last-7d' }
 *   - For custom: { mode: 'custom', customExpressions: { start: 'now-1h', end: 'now' } }
 *
 * @returns {Object} - Object with:
 *   - getTimestamps: () => { from_time, to_time } - Always returns fresh calculated timestamps
 *   - getDisplayValue: () => value for TimeRangePicker component
 */
export const useTimeRangePicker = (timeRangeValue) => {
  // Get the current timestamps (always fresh)
  const getTimestamps = useMemo(() => {
    return () => {
      if (!timeRangeValue) return null;

      // For preset mode: calculate based on preset definition
      if (timeRangeValue.mode === 'preset' && timeRangeValue.presetId) {
        const preset = TIME_RANGE_PRESETS.find(p => p.id === timeRangeValue.presetId);
        if (!preset) return null;

        const startParsed = parseTimeExpression(preset.start);
        const endParsed = parseTimeExpression(preset.end);

        if (startParsed && endParsed) {
          return {
            from_time: startParsed.format('YYYY-MM-DD HH:mm:ss'),
            to_time: endParsed.format('YYYY-MM-DD HH:mm:ss')
          };
        }
      }

      // For custom mode: parse the custom expressions
      if (timeRangeValue.mode === 'custom' && timeRangeValue.customExpressions) {
        const startParsed = parseTimeExpression(timeRangeValue.customExpressions.start);
        const endParsed = parseTimeExpression(timeRangeValue.customExpressions.end);

        if (startParsed && endParsed) {
          return {
            from_time: startParsed.format('YYYY-MM-DD HH:mm:ss'),
            to_time: endParsed.format('YYYY-MM-DD HH:mm:ss')
          };
        }
      }

      return null;
    };
  }, [timeRangeValue]);

  // Get the value to display in TimeRangePicker component
  const getDisplayValue = () => {
    return timeRangeValue;
  };

  return {
    getTimestamps,
    getDisplayValue
  };
};
